import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Plus } from 'lucide-react';
import { getRequest} from '../../../services/apiService';
import RequisitionForm from './RequisitionForm';

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const RequisitionScheduler = ({ selectedProjectId }) => {
  const getMondayOfWeek = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const [showCalendar, setShowCalendar] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return getMondayOfWeek(today);
  });
  const [requisitions, setRequisitions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [previousPageUrl, setPreviousPageUrl] = useState(null);

  const [showRequisitionForm, setShowRequisitionForm] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);

  const calendarRef = useRef(null);

  const fetchRequisitionData = useCallback(async (pageUrl = null) => {
    let apiUrl;

    const startDateFormatted = formatDate(selectedWeekStart);
    const endDate = new Date(selectedWeekStart);
    endDate.setDate(selectedWeekStart.getDate() + 6);
    const endDateFormatted = formatDate(endDate);

    if (pageUrl) {
      apiUrl = pageUrl;
    } else {
      apiUrl = `/pert/requisition-calendar/?start_date=${startDateFormatted}&end_date=${endDateFormatted}&project_id=${selectedProjectId || 12}`;
    }

    try {
      const response = await getRequest(apiUrl);
      const apiData = response.results || response;

      setNextPageUrl(response.next);
      setPreviousPageUrl(response.previous);

      const newRequisitions = [];
      const newTasks = [];
      const seenTaskIds = new Set();

      apiData.forEach(requisitionData => {
        const taskId = requisitionData.task.id;

        if (!seenTaskIds.has(taskId)) {
          newTasks.push({
            id: taskId,
            name: requisitionData.task.name,
            color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0'),
          });
          seenTaskIds.add(taskId);
        }

        newRequisitions.push({
          id: requisitionData.id,
          taskId: taskId,
          date: requisitionData.date,
          name: requisitionData.name,
          remarks: requisitionData.remarks,
          status: requisitionData.status,
          uploadedBy: requisitionData.uploaded_by,
          uploadedAt: requisitionData.uploaded_at,
          reviewComments: requisitionData.review_comments,
          reviewedBy: requisitionData.reviewed_by,
          reviewedAt: requisitionData.reviewed_at,
          project: requisitionData.project_name,
          ship: requisitionData.ship_name,
          task: requisitionData.task.name,
          contributionCalc: requisitionData.task.contribution_calc,
        });
      });

      setTasks(newTasks);
      setRequisitions(newRequisitions);

    } catch (error) {
      console.error('Error fetching requisition data:', error);
    }
  }, [selectedWeekStart, selectedProjectId]);

  useEffect(() => {
    fetchRequisitionData();
  }, [fetchRequisitionData]);

  const handleNextPage = () => {
    if (nextPageUrl) {
      fetchRequisitionData(nextPageUrl);
    }
  };

  const handlePreviousPage = () => {
    if (previousPageUrl) {
      fetchRequisitionData(previousPageUrl);
    }
  };

  const generateWeekDates = () => {
    const dates = [];
    const startDate = new Date(selectedWeekStart);
    startDate.setHours(0, 0, 0, 0);

    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDate(date) === formatDate(today);
  };

  const getRequisitionsForTaskAndDate = (taskId, date) => {
    const localDateStr = formatDate(date);
    return requisitions.filter(req => {
      return req.taskId === taskId && req.date === localDateStr;
    });
  };

  const hexToRgba = (hex, alpha = 0.3) => {
    if (!hex || hex.length !== 7) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // **UPDATED** Function to get color based on requisition status
  const getRequisitionColor = (status) => {
    switch (status) {
      case 'Not Allocated':
        return { background: 'rgba(255, 99, 71, 0.3)', border: '1px solid #FF6347' }; // Tomato Red
      case 'Completed':
        return { background: 'rgba(60, 179, 113, 0.3)', border: '1px solid #3CB371' }; // Medium Sea Green
      case 'Assigned':
        return { background: 'rgba(255, 204, 0, 0.3)', border: '1px solid #FFCC00' }; // Amber/Yellow
      case 'Work in Progress':
        return { background: 'rgba(0, 128, 255, 0.3)', border: '1px solid #0080FF' }; // Bright Blue
      case 'Re-offered':
        return { background: 'rgba(153, 50, 204, 0.3)', border: '1px solid #9932CC' }; // Amethyst/Purple
      default:
        return { background: 'rgba(128, 128, 128, 0.3)', border: '1px solid #808080' }; // Grey for undefined/other statuses
    }
  };

  const handleTodayClick = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const mondayOfToday = getMondayOfWeek(today);
    setSelectedWeekStart(mondayOfToday);
    setCurrentDate(today);

    setTimeout(() => {
      const todayElement = document.querySelector('[data-today="true"]');
      if (todayElement) {
        todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleCalendarDateSelect = (date) => {
    const dateWithoutTime = new Date(date);
    dateWithoutTime.setHours(0, 0, 0, 0);
    const mondayOfSelectedWeek = getMondayOfWeek(dateWithoutTime);
    setSelectedWeekStart(mondayOfSelectedWeek);
    setCurrentDate(dateWithoutTime);
    setShowCalendar(false);
  };

  const handleRequisitionClick = (requisition) => {
    setSelectedRequisition(requisition);
    setShowRequisitionForm(true);
  };

  const handleAddNewRequisition = () => {
    setSelectedRequisition(null);
    setShowRequisitionForm(true);
  };

  const handleCloseRequisitionForm = () => {
    setShowRequisitionForm(false);
    setSelectedRequisition(null);
    fetchRequisitionData();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const weekDates = generateWeekDates();

  const generateCalendarDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const dates = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const calendarDates = generateCalendarDates();

  return (
    <div className="w-full bg-white relative min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between p-2 sm:p-3 lg:p-4 bg-gray-50 border-b sticky top-0 z-40">
        {/* Left Section: Calendar Navigation and Today Button */}
        <div className="flex items-center gap-1 lg:gap-2">
          <button
            onClick={() => setShowCalendar(!showCalendar)}
            className="text-xl font-medium text-blue-600 hover:bg-blue-50 px-3 py-2 rounded flex items-center space-x-2"
          >
            <span>{selectedWeekStart.toLocaleDateString('en-US', {
              month: window.innerWidth < 640 ? 'short' : 'long',
              year: 'numeric'
            })}</span>
            <Calendar className="w-5 h-5" />
          </button>

          {/* Calendar Popup */}
          {showCalendar && (
            <div
              ref={calendarRef}
              className="absolute top-full right-0 sm:right-2 lg:right-4 mt-1 bg-white border rounded-lg shadow-lg z-50 p-3 lg:p-4 w-[280px] sm:w-80 lg:w-96 max-w-[calc(100vw-1rem)]"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base lg:text-lg font-semibold text-blue-500">
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 p-2">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendarDates.map((date, index) => (
                  <button
                    key={index}
                    className={`p-2 text-sm rounded hover:bg-gray-100 ${
                      date.getMonth() !== currentDate.getMonth()
                        ? 'text-gray-300'
                        : 'text-gray-700'
                    } ${
                      isToday(date)
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : ''
                    } ${
                      date.toDateString() === selectedWeekStart.toDateString() ||
                      (date > selectedWeekStart && date < new Date(selectedWeekStart.getTime() + 5 * 24 * 60 * 60 * 1000) && date.getDay() !== 0 && date.getDay() !== 6)
                        ? 'bg-blue-100 text-blue-800'
                        : ''
                    }`}
                    onClick={() => handleCalendarDateSelect(date)}
                  >
                    {date.getDate()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Section: Calendar Select and Add New Requisition Button */}
        <div className="flex items-center gap-2 lg:gap-4">
            <button
            onClick={() => {
              const prevWeek = new Date(selectedWeekStart);
              prevWeek.setDate(selectedWeekStart.getDate() - 7);
              prevWeek.setHours(0, 0, 0, 0);
              setSelectedWeekStart(prevWeek);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            <ChevronLeft size={16} className="text-blue-500 sm:w-5 sm:h-5" />
          </button>
          <button
            onClick={handleTodayClick}
            className="px-2 lg:px-3 py-1 text-blue-500 text-xs sm:text-sm hover:text-blue-600 whitespace-nowrap"
          >
            Today
          </button>
          <button
            onClick={() => {
              const nextWeek = new Date(selectedWeekStart);
              nextWeek.setDate(selectedWeekStart.getDate() + 7);
              nextWeek.setHours(0, 0, 0, 0);
              setSelectedWeekStart(nextWeek);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            <ChevronRight size={16} className="text-blue-500 sm:w-5 sm:h-5" />
          </button>

          {/* Add New Requisition Button */}
              <button
                onClick={handleAddNewRequisition}
                className="ml-4 px-4 py-2 bg-blue-500 text-white rounded-md flex items-center space-x-2 hover:bg-blue-600 transition-colors duration-200"
              >
                <Plus className="w-5 h-5" />
                <span>Add</span>
              </button>
        </div>
      </div>

      {/* Calendar Layout with Fixed Task Column */}
      <div className="flex">
        {/* Fixed Task Column */}
        <div className="w-[200px] bg-white border-r flex-shrink-0">
          {/* Task Header */}
          <div className="p-2 sm:p-3 border-b bg-gray-50">
            <div className="text-xs sm:text-sm font-medium text-gray-600">Tasks</div>
          </div>

          {/* Task List */}
          {tasks.map((task) => (
            <div key={task.id} className="p-2 sm:p-3 lg:p-4 border-b hover:bg-gray-50 h-12 sm:h-16 lg:h-20 flex items-center">
              <div className="flex items-center gap-2 lg:gap-3 w-full">
                <div className="relative flex-shrink-0">
                  <div
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: task.color }}
                  >
                    {task.name.charAt(0)}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-800 text-xs sm:text-sm truncate">{task.name}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Date Headers */}
            <div className="grid grid-cols-5 border-b bg-gray-50">
              {weekDates.map((date, index) => (
                <div key={index} className="border-r" data-today={isToday(date)}>
                  <div className="p-2 sm:p-3 text-center">
                    <div
                      className={`font-medium text-xs sm:text-sm ${
                        isToday(date)
                          ? 'text-blue-600'
                          : 'text-gray-800'
                      }`}
                    >
                      <div className="block sm:hidden">
                        {date.getDate()}
                        <div className="text-xs">{getDayName(date)}</div>
                      </div>
                      <div className="hidden sm:block md:hidden">
                        {`${date.getDate()} ${getDayName(date)}`}
                      </div>
                      <div className="hidden md:block">
                        {`${date.getDate()}  ${date.toLocaleString('default', { month: 'short' })} ${getDayName(date)}`}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Schedule Rows */}
            {tasks.map((task) => (
              <div key={task.id} className="grid grid-cols-5 border-b hover:bg-gray-50">
                {weekDates.map((date, dateIndex) => {
                  const requisitionsForThisDay = getRequisitionsForTaskAndDate(task.id, date);
                  return (
                    <div key={dateIndex} className="border-r">
                      <div className="flex flex-col h-12 sm:h-16 lg:h-20 justify-center items-center p-1">
                        {requisitionsForThisDay.map(req => {
                          const statusColors = getRequisitionColor(req.status);
                          return (
                            <div
                              key={req.id}
                              className="flex items-center justify-between px-1 py-1 rounded text-xs text-center font-medium text-gray-800 w-full max-w-[90%] cursor-pointer"
                              style={statusColors}
                              title={`${req.name} - ${req.remarks || ''} - Status: ${req.status}`}
                              onClick={() => handleRequisitionClick(req)}
                            >
                              <span className="truncate">{req.name}</span>
                              <span className="ml-1 text-gray-700 whitespace-nowrap text-[10px] uppercase font-semibold">
                                  {req.status}
                              </span>
                              {req.contributionCalc && (
                                <span className="ml-1 text-gray-600 whitespace-nowrap">
                                  {req.contributionCalc} %
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center gap-4 p-4 bg-gray-50 border-t">
        <button
          onClick={handlePreviousPage}
          disabled={!previousPageUrl}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            previousPageUrl
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
        >
          Previous
        </button>
        <button
          onClick={handleNextPage}
          disabled={!nextPageUrl}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            nextPageUrl
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
        >
          Next
        </button>
      </div>

      {/* RequisitionForm Pop-up */}
      {showRequisitionForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[70vh] overflow-y-auto">
            <RequisitionForm
              initialData={selectedRequisition}
              onClose={handleCloseRequisitionForm}
              eventStatus={selectedRequisition ? selectedRequisition.status : 'new'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RequisitionScheduler;